import { EXPLORE_ROUTES } from "../exploreRoutes";
import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import HeaderProfileSection from "../HeaderProfileSection";
import GamificationScoreBoard from "../gamificationDetail/GamificationScoreBoard";
import CoinConverterGif from "../../assets/images/Coin-converter.gif";
import { useGamificationBattleSession } from "../../hooks/useGamificationBattleSession";
import { toggleQuestionBookmark } from "../../services/questionBankQuestionService";
import {
  buildGamificationFlowState,
  buildGamificationSubjectsState,
  getStoredGamificationFlowState,
} from "./gamificationFlowState";
import "../questionBank/QbankDetail.css";
import "./GamificationLiveBattle.css";
import "./Powerup.css";
const POWER_UPS = [
  {
    id: "reveal",
    key: "reveal_answer",
    title: "Reveal Answer",
    description: "Instantly reveals the correct option.",
    iconClass: "fa-eye",
    toneClass: "powerup-tone-purple",
  },
  {
    id: "fifty",
    key: "fifty_fifty",
    title: "50-50 Lifeline",
    description: "Removes two wrong options for better focus.",
    iconClass: "fa-clone",
    toneClass: "powerup-tone-blue",
  },
  {
    id: "shield",
    key: "shield",
    title: "Shield",
    description: "Activates defense mode for this question.",
    iconClass: "fa-shield-alt",
    toneClass: "powerup-tone-green",
  },
  {
    id: "hint",
    key: "ai_hint",
    title: "AI Hint Unlock",
    description: "Provides a smart hint based on the question.",
    iconClass: "fa-robot",
    toneClass: "powerup-tone-indigo",
  },
  {
    id: "poll",
    key: "audience_poll",
    title: "Audience Poll",
    description: "Shows audience vote percentages as guidance.",
    iconClass: "fa-chart-column",
    toneClass: "powerup-tone-teal",
  },
];

function normalizeLanguage(value) {
  const normalizedValue = String(value || "").toUpperCase();
  return normalizedValue === "HI" || normalizedValue === "HINDI" ? "HI" : "EN";
}

function getLocalizedText(item = {}, baseField, selectedLang = "EN") {
  const languageSuffix = normalizeLanguage(selectedLang) === "HI" ? "hi" : "en";
  const fallbackSuffix = languageSuffix === "hi" ? "en" : "hi";
  return item?.[`${baseField}_${languageSuffix}`] || item?.[baseField] || item?.[`${baseField}_${fallbackSuffix}`] || "";
}

function getQuestionHtml(question, selectedLang = "EN") {
  if (!question) {
    return "Waiting for question...";
  }

  if (normalizeLanguage(selectedLang) === "HI") {
    return (
      question.question_name_hi ||
      question.question_statement_one_hi ||
      question.question_hi ||
      question.question_text_hi ||
      question.question_name ||
      question.question_statement_one ||
      question.question_statement_two ||
      question.question ||
      question.question_text ||
      "Waiting for question..."
    );
  }

  return (
    question.question_name_en ||
    question.question_name ||
    question.question_statement_one_en ||
    question.question_statement_one ||
    question.question_statement_two ||
    question.question_en ||
    question.question ||
    question.question_text_en ||
    question.question_text ||
    "Waiting for question..."
  );
}

function isAffirmativeFlag(value) {
  return ["1", "Y", "YES", "TRUE"].includes(String(value ?? "").toUpperCase());
}

function getOptionCorrectness(option = {}) {
  const rawValue =
    option?.is_option_correct ??
    option?.is_correct ??
    option?.correct_option ??
    option?.correct ??
    option?.answer_status;

  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return null;
  }

  return isAffirmativeFlag(rawValue);
}

function normalizeOption(option = {}, index = 0, selectedLang = "EN") {
  return {
    id: Number(option?.option_id || option?.id || index + 1),
    label:
      getLocalizedText(option, "option_name", selectedLang) ||
      option?.option_value ||
      option?.option ||
      option?.column_1 ||
      `Option ${index + 1}`,
    isCorrect: getOptionCorrectness(option),
    english: option?.option_name_en || option?.option_name || "",
    hindi: option?.option_name_hi || "",
  };
}

function playAnswerTone(tone) {
  if (typeof window === "undefined") {
    return;
  }

  const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextConstructor) {
    return;
  }

  const audioContext = new AudioContextConstructor();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = tone === "correct" ? 760 : 220;
  gain.gain.setValueAtTime(0.001, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.12, audioContext.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.18);
  oscillator.connect(gain);
  gain.connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.2);
  window.setTimeout(() => audioContext.close(), 260);
}

function getAvailableLanguages(question, selectedLang) {
  const availableLanguages = Array.isArray(question?.available_languages)
    ? question.available_languages
    : Array.isArray(question?.next_question_available_langs)
      ? question.next_question_available_langs
      : [];
  const normalizedLanguages = availableLanguages.map(normalizeLanguage);
  const languageSet = new Set(["EN", ...normalizedLanguages, normalizeLanguage(selectedLang)]);

  if (
    question?.question_name_hi ||
    question?.question_statement_one_hi ||
    question?.question_hi ||
    question?.question_text_hi
  ) {
    languageSet.add("HI");
  }

  return Array.from(languageSet);
}

function getLanguageLabel(language) {
  return normalizeLanguage(language) === "HI" ? "Hindi" : "English";
}

function getPowerUpMessage(powerUpKey) {
  if (powerUpKey === "shield") {
    return "Shield active for this question.";
  }

  if (powerUpKey === "fifty_fifty") {
    return "50-50 power-up requested.";
  }

  if (powerUpKey === "reveal_answer") {
    return "Reveal power-up requested.";
  }

  if (powerUpKey === "ai_hint") {
    return "AI hint requested.";
  }

  if (powerUpKey === "audience_poll") {
    return "Poll requested.";
  }

  return "Power-up requested.";
}

const GamificationLiveBattle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOptionId, setSelectedOptionId] = useState(0);
  const [isGuessAnswer, setIsGuessAnswer] = useState(false);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [usedPowerUpKey, setUsedPowerUpKey] = useState("");
  const [fontScale, setFontScale] = useState(1);
  const [bookmarkStatus, setBookmarkStatus] = useState(false);
  const [isBookmarkPending, setIsBookmarkPending] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [sheetHeight, setSheetHeight] = useState(() => Math.min(window.innerHeight * 0.62, 620));
  const [sheetDragging, setSheetDragging] = useState(false);
  const sheetDragStartY = useRef(0);
  const sheetDragStartHeight = useRef(0);
  const [isPyqOpen, setIsPyqOpen] = useState(true);
  const [isExplanationOpen, setIsExplanationOpen] = useState(true);
  const [shieldChances, setShieldChances] = useState(0);
  const [showPowerUpModal, setShowPowerUpModal] = useState(false);
  const [powerUpMessage, setPowerUpMessage] = useState("");
  const [requestedPowerUpKey, setRequestedPowerUpKey] = useState('');
  const powerUpRequestRef = useRef(false);
  const powerUpModalRef = useRef(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showOpponentsLeftModal, setShowOpponentsLeftModal] = useState(false);
  const flowState = useMemo(() => {
    const hasLocationState = Boolean(location.state && Object.keys(location.state).length);
    return buildGamificationFlowState(location.state || {}, hasLocationState ? {} : getStoredGamificationFlowState());
  }, [location.state]);
  const {
    state,
    questionTimeLeft,
    questionTimeTaken,
    submitAnswer,
    requestBattleScore,
    usePowerUp: triggerPowerUp,
    leaveGame,
    completeGame,
    disconnect,
    syncFlowState,
  } = useGamificationBattleSession(flowState);

  const selectedBattle = state?.selectedBattle || {};
  const selectedSubject = state?.selectedSubject || {};
  const currentQuestion = state?.currentQuestion || null;
  const selectedLang = normalizeLanguage(state?.selected_lang || currentQuestion?.selected_lang || "EN");
  const availableLanguages = getAvailableLanguages(currentQuestion, selectedLang);
  const questionOptions = Array.isArray(currentQuestion?.question_option)
    ? currentQuestion.question_option.map((option, index) => normalizeOption(option, index, selectedLang))
    : [];
  const battleName =
    selectedBattle?.battles_name ||
    selectedBattle?.battle_name ||
    selectedBattle?.title ||
    "Battle";
  const subjectName =
    selectedSubject?.subject_name || selectedSubject?.title || "Subject";
  const currentCoins = Number(
    currentQuestion?.remaining_coins ??
      state?.remaining_coins ??
      state?.userCoins ??
      state?.user_coins ??
      0
  );
  const isWaitingForOthers = state?.liveStage === "waiting_for_answer" && state?.answerSubmitted;
  const isAnswerLocked = Boolean(state?.answerSubmitted || selectedOptionId);
  const effect = Number(state?.power_up_state?.question_id) === Number(currentQuestion?.question_id)
    ? state?.power_up_state?.effect_payload || {} : {};
  const hiddenOptionIds = new Set((effect.hide_option_ids || []).map(Number));
  const pollByOption = new Map((effect.poll || []).map((item) => [Number(item.option_id), Number(item.percentage || 0)]));
  const availablePowerUps = Array.isArray(currentQuestion?.power_ups_available) ? currentQuestion.power_ups_available : [];
  const usedPowerUpKeys = Array.isArray(currentQuestion?.power_ups_used_keys) ? currentQuestion.power_ups_used_keys : [];
  const powerUpChoices = POWER_UPS.map(presentation => {
    const settings = availablePowerUps.find(item => item.power_up_key === presentation.key);
    const cost = settings?.coin_cost == null ? null : Number(settings.coin_cost);
    const used = Math.max(0, Number(settings?.used_in_battle || 0));
    const limit = Number(settings?.per_battle_user_limit || 0);
    const remaining = settings?.remaining_uses_in_battle == null ? Math.max(0, limit - used) : Number(settings.remaining_uses_in_battle);
    const usedHere = isAffirmativeFlag(settings?.is_used) || usedPowerUpKeys.includes(presentation.key) || usedPowerUpKey === presentation.key;
    const configured = settings && isAffirmativeFlag(settings.is_enabled) && cost !== null && Number.isFinite(cost) && cost >= 0;
    const exhausted = limit > 0 && remaining <= 0;
    const affordable = cost !== null && currentCoins >= cost;
    const pending = state?.powerUpPending || Boolean(requestedPowerUpKey);
    const enabled = configured && isAffirmativeFlag(settings.is_usable_now) && !usedHere && !exhausted && affordable &&
      !isAnswerLocked && !usedPowerUpKey && !usedPowerUpKeys.length && !pending && state?.isSocketConnected;
    const status = requestedPowerUpKey === presentation.key ? 'APPLYING...' : usedHere ? 'USED' : !configured ? 'UNAVAILABLE' :
      exhausted ? 'LIMIT REACHED' : !affordable ? 'NOT ENOUGH COINS' : enabled ? 'READY' : 'UNAVAILABLE';
    const suffix = selectedLang === 'HI' ? 'hi' : 'en';
    return { ...presentation,
      title: settings?.[`power_up_name_${suffix}`] || settings?.power_up_name || presentation.title,
      description: settings?.[`power_up_description_${suffix}`] || settings?.power_up_description || presentation.description,
      cost, status, enabled,
      usageLabel: !settings ? '— used' : limit > 0 ? `${used}/${limit} used` : `${used} used`,
      remainingLabel: !settings ? 'Unavailable' : limit > 0 ? `${Math.max(0, remaining)} left` : 'Unlimited',
    };
  });
  const scores = state?.liveScore?.score_list || [];
  const scorePlayers = scores.length ? scores : (state?.users_list || []).map((player) => ({
    user_id: player.user_id,
    user_name: player.name || player.user_name,
    profile_pics: player.profile_pics,
    total_points: "0",
  }));
  const questionEnglish = currentQuestion ? getQuestionHtml(currentQuestion, "EN") : "";
  const questionHindi = currentQuestion?.question_name_hi || currentQuestion?.question_statement_one_hi || "";
  const explanation = getLocalizedText(currentQuestion || {}, "explanation", selectedLang) ||
    currentQuestion?.answer_explanation || "";

  useEffect(() => {
    if (!state?.powerUpPending) {
      powerUpRequestRef.current = false;
      setRequestedPowerUpKey('');
    }
    if (state?.socketError) setPowerUpMessage('');
  }, [state?.powerUpPending, state?.socketError, currentQuestion?.question_id]);

  useEffect(() => {
    if (!showPowerUpModal) return undefined;
    const previousFocus = document.activeElement;
    powerUpModalRef.current?.querySelector('button:not(:disabled)')?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') setShowPowerUpModal(false);
      if (event.key !== 'Tab') return;
      const buttons = [...(powerUpModalRef.current?.querySelectorAll('button:not(:disabled)') || [])];
      const first = buttons[0]; const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); previousFocus?.focus?.(); };
  }, [showPowerUpModal]);

  useEffect(() => {
    setSelectedOptionId(0);
    setIsGuessAnswer(false);
    setUsedPowerUpKey(currentQuestion?.power_ups_used_keys?.[0] || "");
    setShieldChances(0);
    setBookmarkStatus(Number(currentQuestion?.bookmark_status || 0) === 1);
    setPowerUpMessage("");
    setShowPowerUpModal(false);
  }, [currentQuestion?.question_id, currentQuestion?.bookmark_status, currentQuestion?.power_ups_used_keys]);

  useEffect(() => {
    const powerUp = state?.power_up_state;
    if (!powerUp || Number(powerUp.question_id) !== Number(currentQuestion?.question_id)) return;
    setUsedPowerUpKey(powerUp.power_up_key || "");
    const shield = powerUp.effect_payload || {};
    const protectsWrong = !shield.second_chance_on || /wrong|incorrect|miss/i.test(shield.second_chance_on);
    setShieldChances(powerUp.power_up_key === "shield" && isAffirmativeFlag(shield.shield_active) &&
      protectsWrong && (isAffirmativeFlag(shield.second_chance_enabled) || Number(shield.second_chance_total) > 0)
      ? Number(shield.second_chance_remaining || 0) : 0);
    setPowerUpMessage(powerUp.effect_payload?.hint_text || getPowerUpMessage(powerUp.power_up_key));
    setShowPowerUpModal(false);
  }, [state?.power_up_state, currentQuestion?.question_id]);

  const handleBookmark = async () => {
    if (!currentQuestion?.question_id || isBookmarkPending) return;
    setIsBookmarkPending(true);
    try {
      const result = await toggleQuestionBookmark({ question_id: Number(currentQuestion.question_id) });
      setBookmarkStatus(Number(result?.payload?.bookmark_status || 0) === 1);
    } catch (_error) {
      // Keep the server-confirmed bookmark state.
    } finally {
      setIsBookmarkPending(false);
    }
  };

  const openScoreboard = () => {
    requestBattleScore();
    setSheetHeight(Math.min(window.innerHeight * 0.62, 620));
    setShowScoreModal(true);
  };

  const handleSheetPointerDown = (event) => {
    sheetDragStartY.current = event.clientY;
    sheetDragStartHeight.current = sheetHeight;
    setSheetDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleSheetPointerMove = (event) => {
    if (!sheetDragging) return;
    setSheetHeight(Math.max(0, Math.min(window.innerHeight * 0.92,
      sheetDragStartHeight.current + sheetDragStartY.current - event.clientY)));
  };

  const handleSheetPointerUp = () => {
    if (!sheetDragging) return;
    setSheetDragging(false);
    if (sheetHeight < 180) setShowScoreModal(false);
    else setSheetHeight(Math.max(260, sheetHeight));
  };

  useEffect(() => {
    if (state?.leaveGameResponse?.isAllUserLeave === true) {
      setShowOpponentsLeftModal(true);
    }
  }, [state?.leaveGameResponse]);

  const handleOpponentsLeft = () => {
    completeGame();
    setShowOpponentsLeftModal(false);
    navigate(EXPLORE_ROUTES.subjects, { state: buildGamificationSubjectsState(state) });
  };

  useEffect(() => {
    if (state?.liveStage === "completed" && state?.battle_room_id) {
      navigate(GAMIFICATION_ROUTES.battleResult, {
        state: {
          ...state,
          battles_id: Number(state?.battles_id || 0),
          battle_room_id: Number(state?.battle_room_id || 0),
          subject_id: Number(state?.subject_id || 0),
          states_id: Number(state?.state_id || 0),
        },
      });
    }
  }, [navigate, state]);

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setShowLeaveModal(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleLeave = () => {
    leaveGame();
    disconnect(true);
    navigate(EXPLORE_ROUTES.subjects, {
      state: buildGamificationSubjectsState(state),
    });
  };

  const handleAnswerClick = (option) => {
    if (!currentQuestion?.question_id || isAnswerLocked) {
      return;
    }

    if (usedPowerUpKey === "shield" && shieldChances > 0 && option.isCorrect === false) {
      setShieldChances((current) => current - 1);
      setPowerUpMessage("Shield saved this attempt. Choose again.");
      if (isSoundEnabled) {
        playAnswerTone("wrong");
      }
      return;
    }

    setSelectedOptionId(option.id);
    if (isSoundEnabled && option.isCorrect !== null) {
      playAnswerTone(option.isCorrect ? "correct" : "wrong");
    }
    const didSubmit = submitAnswer(option.id, isGuessAnswer ? 1 : 0);
    if (!didSubmit) {
      setSelectedOptionId(0);
    }
  };

  const handlePowerUpClick = (powerUpKey) => {
    if (!currentQuestion?.question_id || powerUpRequestRef.current || !powerUpChoices.find(item => item.key === powerUpKey)?.enabled) {
      return;
    }

    const didSend = triggerPowerUp(powerUpKey);
    if (didSend) {
      powerUpRequestRef.current = true;
      setRequestedPowerUpKey(powerUpKey);
      setPowerUpMessage("Applying power-up...");
    }
  };

  const handleLanguageChange = (language) => {
    const nextLanguage = normalizeLanguage(language);
    syncFlowState({
      selected_lang: nextLanguage,
      currentQuestion: currentQuestion
        ? {
            ...currentQuestion,
            selected_lang: nextLanguage,
          }
        : currentQuestion,
    });
  };

  return (
    <>
      <style>{".homeDashboard-wrapper .main-content { height: 100vh; overflow: hidden; box-sizing: border-box; padding-bottom: 35px; } .homeDashboard-wrapper .page-content { height: 100%; overflow: hidden; padding-right: 0; }"}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="categories-page qbank-detail-page gamification-designer-page" style={{ background: "#fff" }}>
          <div className="categories-page-header">
            <div>
              <button type="button" className="categories-back-button" aria-label="Back" onClick={() => setShowLeaveModal(true)}>
                <i className="bi bi-chevron-left" aria-hidden="true" />
              </button>
            </div>
            <h1 className="categories-page-title">
              {String(Math.floor(questionTimeLeft / 3600)).padStart(2, "0")}:
              {String(Math.floor((questionTimeLeft % 3600) / 60)).padStart(2, "0")}:
              {String(questionTimeLeft % 60).padStart(2, "0")}
            </h1>
            <div><HeaderProfileSection /></div>
          </div>

          <div className="qb-Detail-content scoreboard-sidebar">
            <div className="d-flex qbank-detail-main">
              <section className="qbank-LeftContent" aria-label="Battle question and answers">
                <div className="qbank-card qbank-card--question gamification-live-card">
                  <div className="qbank-questionTop">
                    <span className="qbank-progress">
                      {Number(currentQuestion?.question_index || 0)} of {Number(currentQuestion?.total_questions || 0)}
                    </span>
                    <div className="qbank-timer" aria-label="Question timer">
                      <i className="bi bi-clock" aria-hidden="true" />
                      <span>{String(Math.floor(questionTimeLeft / 60)).padStart(2, "0")}:{String(questionTimeLeft % 60).padStart(2, "0")}</span>
                    </div>
                    <div className="qbank-toolbar" aria-label="Question tools">
                      <button type="button" className="qbank-toolbarBtn" onClick={() => setIsSoundEnabled((value) => !value)} aria-label={isSoundEnabled ? "Mute sound" : "Enable sound"} aria-pressed={isSoundEnabled}>
                        <i className={"bi " + (isSoundEnabled ? "bi-volume-up-fill" : "bi-volume-mute-fill")} aria-hidden="true" />
                      </button>
                      <button type="button" className="qbank-toolbarBtn" onClick={() => setFontScale((size) => size >= 1.4 ? 0.85 : Math.min(1.4, size + 0.1))} aria-label="Change text size">
                        <i className="bi bi-type" aria-hidden="true" />
                      </button>
                      <button type="button" className="qbank-toolbarBtn" onClick={handleBookmark} disabled={!currentQuestion || isBookmarkPending} aria-label={bookmarkStatus ? "Remove bookmark" : "Bookmark question"} aria-pressed={bookmarkStatus}>
                        <i className={"bi " + (bookmarkStatus ? "bi-bookmark-fill" : "bi-bookmark")} aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="gamification-designer-meta">
                    <span>{battleName}</span>
                    <span>{subjectName}</span>
                    <span><img src={CoinConverterGif} alt="" />{currentCoins} coins</span>
                    <span>Time taken: {questionTimeTaken}s</span>
                    <div className="gamification-language-toggle" aria-label="Question language">
                      {availableLanguages.map((language) => (
                        <button key={language} type="button" className={selectedLang === language ? "is-active" : ""} onClick={() => handleLanguageChange(language)} aria-pressed={selectedLang === language}>
                          {getLanguageLabel(language)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {state?.socketError ? <div className="gamification-battle-status">{state.socketError}</div> : null}
                  {!state?.socketError && state?.connectionStatus === "connecting" ? <div className="gamification-battle-status">Connecting to battle...</div> : null}

                  <div className="data-question-scroll">
                    {currentQuestion ? (
                      <>
                        <div className="qbank-questionBlock" style={{ fontSize: String(fontScale) + "em" }}>
                          <h2 className="qbank-question" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionEnglish) }} />
                          {questionHindi && questionHindi !== questionEnglish ? (
                            <p className="qbank-questionSub" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionHindi) }} />
                          ) : null}
                          {currentQuestion.question_image ? <img className="gamification-question-image" src={currentQuestion.question_image} alt="Question illustration" /> : null}
                        </div>

                        <div className="gamification-battle-powerups">
                          <button type="button" className={"gamification-battle-powerup" + (usedPowerUpKey ? " is-used" : "")} onClick={() => setShowPowerUpModal(true)} disabled={isAnswerLocked || Boolean(usedPowerUpKey) || state?.powerUpPending || !state?.isSocketConnected}>
                            <i className="bi bi-lightning-charge-fill" aria-hidden="true" />
                            {usedPowerUpKey ? "Power-up used" : "Power-ups"}
                          </button>
                        </div>
                        {powerUpMessage ? <div className="gamification-battle-status">{powerUpMessage}</div> : null}
                        {state?.answerSubmitted && !state?.answerSaveResponse ? <div className="gamification-battle-status">Submitting answer...</div> : null}
                        {isWaitingForOthers ? <div className="gamification-battle-status">Answer saved. Waiting for other players to submit.</div> : null}

                        <div className="qbank-options" role="list" aria-label="Answer options">
                          {questionOptions.filter((option) => !hiddenOptionIds.has(option.id)).map((option, index) => (
                            <button
                              key={option.id || index}
                              type="button"
                              disabled={isAnswerLocked}
                              onClick={() => handleAnswerClick(option)}
                              className={"qbank-option gamification-live-option qbank-option--" + (selectedOptionId === option.id ? option.isCorrect === true ? "correct" : option.isCorrect === false ? "wrong" : "neutral" : "neutral")}
                              aria-label={"Option " + String.fromCharCode(65 + index)}
                            >
                              <span className="qbank-option__badge">{String.fromCharCode(65 + index)}</span>
                              <span className="qbank-option__copy">
                                <span className="qbank-option__english" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.english || option.label) }} />
                                {option.hindi && option.hindi !== option.english ? <span className="qbank-option__hindi" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.hindi) }} /> : null}
                              </span>
                              {Number(effect.correct_option_id) === option.id ? <strong>Correct</strong> : null}
                              {pollByOption.has(option.id) ? <strong>{pollByOption.get(option.id)}%</strong> : null}
                            </button>
                          ))}
                        </div>
                        <label className="qbank-guess" htmlFor="gamification-guess-answer">
                          <span className="qbank-guess__check">
                            <input id="gamification-guess-answer" type="checkbox" checked={isGuessAnswer} onChange={(event) => setIsGuessAnswer(event.target.checked)} disabled={isAnswerLocked} />
                            <span className="qbank-guess__box" aria-hidden="true" />
                          </span>
                          <span className="qbank-guess__label">Guess Answer</span>
                          <i className="bi bi-info-circle-fill" aria-hidden="true" />
                        </label>
                      </>
                    ) : <div className="gamification-battle-status">Waiting for battle question...</div>}
                  </div>

                  <div className="footer-nav-fixed footer-nav-fixed--desktop">
                    <div className="qbank-navRow score-fixed-btn">
                      <button type="button" className="qbank-navBtn qbank-navBtn--next" onClick={openScoreboard}>
                        <span>Score</span><i className="bi bi-arrow-right" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <aside className="qbank-rightContent gamification-live-details" aria-label="Question details">
                <section className="qbank-sideCard">
                  <div className="qbank-sideCard__head">
                    <h3>PYP</h3>
                    <button type="button" className="qbank-sideCard__toggle" aria-label="Toggle PYP" onClick={() => setIsPyqOpen((value) => !value)}>
                      <i className={"bi " + (isPyqOpen ? "bi-chevron-up" : "bi-chevron-down")} aria-hidden="true" />
                    </button>
                  </div>
                  {isPyqOpen ? <div className="qbank-sideCard__body">
                    {currentQuestion?.pyq_mappings?.length ? currentQuestion.pyq_mappings.map((exam, index) => (
                      <div className="qbank-sideCard__subtitle" key={index}>
                        <span>{[exam.exam_category, exam.master_exam, exam.year].filter(Boolean).join(" | ")}</span>
                      </div>
                    )) : <div className="qbank-sideCard__subtitle">No PYP details for this question.</div>}
                  </div> : null}
                </section>
                <section className="qbank-sideCard">
                  <div className="qbank-sideCard__head">
                    <h3>Explanation</h3>
                    <button type="button" className="qbank-sideCard__toggle" aria-label="Toggle Explanation" onClick={() => setIsExplanationOpen((value) => !value)}>
                      <i className={"bi " + (isExplanationOpen ? "bi-chevron-up" : "bi-chevron-down")} aria-hidden="true" />
                    </button>
                  </div>
                  {isExplanationOpen ? <div className="qbank-sideCard__body">
                    <div className="qbank-sideCard__subtitle">
                      {explanation ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(explanation) }} /> : <span>Explanation will appear when available.</span>}
                    </div>
                  </div> : null}
                </section>
              </aside>
            </div>

            <div className="footer-nav-fixed footer-nav-fixed--mobile">
              <div className="qbank-navRow score-fixed-btn">
                <button type="button" className="qbank-navBtn qbank-navBtn--next" onClick={openScoreboard}>
                  <span>Score</span><i className="bi bi-arrow-right" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="Qbank-sidebar qbank-detail-sidebar qbank-detail-sidebar--desktop">
              <GamificationScoreBoard players={scorePlayers} />
            </div>
          </div>

          {showScoreModal ? (
            <div className="scoreboard-mobile-overlay" onClick={() => setShowScoreModal(false)}>
              <div className="scoreboard-mobile-sheet" style={{ height: String(sheetHeight) + "px", transition: sheetDragging ? "none" : "height 0.28s ease-out" }} onClick={(event) => event.stopPropagation()}>
                <div className="scoreboard-sheet-drag-area" onPointerDown={handleSheetPointerDown} onPointerMove={handleSheetPointerMove} onPointerUp={handleSheetPointerUp} onPointerCancel={handleSheetPointerUp}>
                  <div className="scoreboard-sheet-handle" />
                </div>
                <button type="button" className="scoreboard-sheet-close" aria-label="Close Scoreboard" onClick={() => setShowScoreModal(false)}><i className="bi bi-x-lg" /></button>
                <div className="scoreboard-mobile-content"><GamificationScoreBoard players={scorePlayers} /></div>
              </div>
            </div>
          ) : null}
        </div>

        {showLeaveModal ? (
          <div className="gamification-battle-modal">
            
            <div className="gamification-battle-modal__card">
              <h3>Leave Battle</h3>
              <p>If you leave now, the active battle session will be closed for you.</p>
              <div className="gamification-battle-modal__actions">
                <button type="button" className="gamification-battle-modal__secondary" onClick={() => setShowLeaveModal(false)}>Stay</button>
                <button type="button" className="gamification-battle-modal__primary" onClick={handleLeave}>Leave</button>
              </div>
            </div>
          </div>
        ) : null}

        {showOpponentsLeftModal ? (
          <div className="gamification-battle-modal">
            <div className="gamification-battle-modal__card">
              <h3>Opponents left</h3>
              <p>All other players have left the battle.</p>
              <div className="gamification-battle-modal__actions">
                <button type="button" className="gamification-battle-modal__primary" onClick={handleOpponentsLeft}>Continue</button>
              </div>
            </div>
          </div>
        ) : null}

  
{showPowerUpModal ? (
   <div className="powerup-modal-overlay"  onClick={() => setShowPowerUpModal(false)}>
      <div className="powerup-modal-card" ref={powerUpModalRef} role="dialog" aria-modal="true" aria-labelledby="battle-powerup-title" onClick={(e) => e.stopPropagation()}>
        <div className="powerup-modal-header">
          <div className="powerup-title-wrap">
            <span className="powerup-title-icon text-purple">
              <i className="fa-solid fa-bolt" />
            </span>
            <h5 id="battle-powerup-title" className="powerup-title blackText fw-bold mb-0">Power-Ups</h5>
          </div>
       
        <button
          type="button"
          className="powerup-close"
          onClick={() => setShowPowerUpModal(false)}
          aria-label="Close power-ups popup"
        >
          <i className="fa-solid fa-xmark" />
        </button>
        </div>

        <div className="powerup-subhead">
          <span className="fw-semibold">Choose one special helper for this question</span>
          <span className="powerup-coin-pill">
             <span><img src={CoinConverterGif} alt="" /></span>
            <span aria-label="Available power-up coins">{currentCoins}</span>
          </span>
        </div>

        <div className="powerup-list">
          {powerUpChoices.map((item) => (
            <button type="button" className={`powerup-item ${item.toneClass}`} key={item.id} aria-label={`Use ${item.title}`} disabled={!item.enabled} onClick={() => handlePowerUpClick(item.key)}>
              <div className="powerup-content">
                <div className="powerup-top-row">
                  <div className="d-flex gap-2 align-items-center">
                    <div className={`powerup-icon ${item.toneClass}`}>
                       <i className={`fa-solid ${item.iconClass}`} />
                   </div> 
                    <div className="">
                      <div className="powerup-name fw-bold blackText">{item.title}</div>
                     
                    </div>
                  </div>
                  <div className="powerup-coins">
                    {item.cost ?? '—'} <span className="coins-color"> Coins </span>
                  </div>
                </div>
                <p className="powerup-desc">{item.description}</p>
                <div className="powerup-bottom-row">
                   <div className="powerup-status">{item.status}</div>
                  <span className="powerup-used blackText fw-semibold">{item.usageLabel}</span>
                  <span className="powerup-left fw-bold">{item.remainingLabel}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
) : null}

      </HomeDashboardLayout>
    </>
  );
};

export default GamificationLiveBattle;


